import { ApolloClient, HttpLink } from '@apollo/client'
import { InMemoryCache } from '@apollo/client/cache'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { PersistedQueryLink } from '@apollo/client/link/persisted-queries'
import { RetryLink } from '@apollo/client/link/retry'
import { sha256 } from 'crypto-hash'
import { isbot } from 'isbot'
import { GetServerSidePropsContext, PreviewData } from 'next'
import nookies, { destroyCookie, parseCookies } from 'nookies'
import { defer, mergeMap } from 'rxjs'
import config from './config'
import serverConfig from './config/server'
import {
  getAttributionHeaders,
  sanitizeAttributionHeaders
} from './utils/attributionHeaders'
import { shouldSuppressCapabilityTelemetry } from './utils/capabilityPrivacy'
import { getExternalReferrer } from './utils/utms'

export const BOT =
  'mozilla/5.0 (macintosh; intel mac os x 10_15_7) applewebkit/537.36 (khtml, like gecko) chrome/94.0.4590.2 safari/537.36 chrome-lighthouse'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const FAKE_CTX = { req: { headers: { 'user-agent': BOT } } } as any

// Use process.env.NODE_ENV so webpack statically replaces it at build time
// and dead-code-eliminates @apollo/client/dev (~20 kB) from production bundles.
if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { loadErrorMessages, loadDevMessages } = require('@apollo/client/dev')
  loadDevMessages()
  loadErrorMessages()
}

/**
 * The session token is now stored in an httpOnly cookie set by
 * /api/session. These helpers delegate to that endpoint — callers
 * don't need to change.
 *
 * TOKEN_COOKIE_OPTIONS is kept exported for backwards compatibility with
 * any lingering external importer, but it is unused in the session flow.
 */
const TOKEN_COOKIE_OPTIONS = {
  maxAge: 30 * 24 * 60 * 60,
  path: '/',
  secure: config.isProd,
  sameSite: 'lax' as const
}

function removeTokenCookie(): void {
  if (typeof window === 'undefined') return
  // `auth-present` is the client-readable twin of the httpOnly `token`
  // cookie: /api/session sets and clears both together with the same 30-day
  // maxAge (since 2026-04-21, longer ago than that maxAge), so its absence
  // means there is no session cookie to clear. AuthProvider reaches this on
  // EVERY anonymous pageview (onIdTokenChanged(null) on landing), which made
  // the DELETE a no-op Vercel function invocation per visit. Skip it then; a
  // logout still carries the sentinel and still clears server-side.
  const hadSession = Boolean(parseCookies()['auth-present'])
  // Clear the sentinel locally first for instant UI feedback, before the
  // network response returns.
  destroyCookie(null, 'auth-present', { path: '/' })
  if (!hadSession) return
  fetch('/api/session', {
    method: 'DELETE',
    credentials: 'include'
  }).catch(() => undefined)
}

function getCookies(context?: GetServerSidePropsContext) {
  return typeof window === 'undefined' ? nookies.get(context) : parseCookies()
}

function extractStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined
  const e = error as {
    statusCode?: number
    status?: number
    response?: { status?: number }
  }
  return e.statusCode ?? e.status ?? e.response?.status
}

function extractRetryAfterMs(error: unknown): number | null {
  if (!error || typeof error !== 'object') return null
  const e = error as {
    response?: { headers?: Headers | Record<string, string> }
  }
  const headers = e.response?.headers
  if (!headers) return null
  const raw =
    typeof (headers as Headers).get === 'function'
      ? (headers as Headers).get('retry-after')
      : (headers as Record<string, string>)['retry-after']
  if (!raw) return null
  const seconds = Number(raw)
  if (!Number.isFinite(seconds)) return null
  // Cap at 60s so the build/runtime doesn't stall indefinitely on a
  // long rate-limit window; the remaining attempts still back off.
  return Math.min(Math.max(seconds, 1), 60) * 1000
}

const getApolloClient = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  context?: GetServerSidePropsContext<any, PreviewData>
): ApolloClient => {
  const httpLink = new HttpLink({ uri: config.graphqlUrl })

  const authLink = setContext(async (_, { headers }) => {
    const cookies = getCookies(context)

    // firebase/auth is ~115 kB — dynamic-import it only on the client where it
    // is actually used, so it doesn't bloat _app.js on every pageview.
    let token: string | undefined
    if (context) {
      token = cookies.token
    } else {
      // Ensure firebase.initializeApp() has run before getAuth(). Both imports
      // resolve in parallel and the firebaseClient init is idempotent.
      const [, { getAuth }] = await Promise.all([
        import('./firebase/firebaseClient'),
        import('firebase/auth')
      ])
      token = await getAuth().currentUser?.getIdToken()
    }
    // Client-side cookie refresh removed — the httpOnly session cookie
    // is now kept in sync by the onIdTokenChanged listener in AuthProvider,
    // which POSTs fresh tokens to /api/session on every Firebase refresh.

    // Ensure session_id / visitor_id exist before any mutation goes out.
    // Scripts.tsx ALSO seeds these, but its useEffect runs AFTER child
    // components' effects (React runs effects bottom-up), so a trackEvent
    // firing from LandingViewTracker on first render would race and send
    // with no session-id header — the server then rejects with
    // "missing sessionId" and we lose the entry-LP attribution event.
    // Self-healing here closes the gap; Scripts.tsx still refreshes maxAge.
    const isClient = typeof window !== 'undefined'
    let sessionId = cookies.session_id
    if (!sessionId && isClient) {
      sessionId = Date.now().toString()
      nookies.set(null, 'session_id', sessionId, {
        maxAge: 60 * 60 * 24 * 30,
        path: '/'
      })
    }
    let visitorId = cookies.visitor_id
    if (!visitorId && isClient && typeof crypto?.randomUUID === 'function') {
      visitorId = crypto.randomUUID()
      nookies.set(null, 'visitor_id', visitorId, {
        maxAge: 365 * 24 * 60 * 60,
        path: '/'
      })
    }

    const privateDocument = shouldSuppressCapabilityTelemetry()
    const attributionUrl = context?.req
      ? (context.req.url ?? '')
      : isClient
        ? window.location.pathname + window.location.search
        : ''
    const utmParams = getAttributionHeaders({
      cookies,
      url: attributionUrl,
      // Client: never our own site — a document navigation from another
      // SpotMe page (e.g. a Pages ↔ App Router crossing) is not a referral.
      referrer: context?.req
        ? context.req.headers.referer
        : getExternalReferrer(),
      privateDocument
    })

    const userAgent = context
      ? context.req.headers['user-agent']
      : typeof navigator !== 'undefined'
        ? navigator.userAgent
        : undefined
    const bot = isbot(userAgent)
    // Only accessed when `context` is truthy (SSR). serverConfig values
    // resolve to undefined in the client bundle anyway (non-NEXT_PUBLIC env
    // vars are stripped by Next.js at build time).
    const clientSecret = context ? serverConfig.clientSecret : undefined

    return {
      headers: {
        ...sanitizeAttributionHeaders(headers, attributionUrl, privateDocument),
        authorization: token ? `Bearer ${token}` : '',
        currency: cookies.currency,
        locale: cookies.locale,
        'session-id': sessionId,
        'visitor-id': visitorId,
        'is-bot': bot.toString(),
        'client-secret': clientSecret,
        ...utmParams
      }
    }
  })

  // Silently refresh the Firebase ID token when an authenticated mutation
  // fails with UNAUTHENTICATED. The httpOnly session cookie is kept fresh by
  // AuthProvider's onIdTokenChanged listener, but a tab that's been idle for
  // >1h with the listener stalled can still send a stale token (observed
  // mid-KYC in May 2026 — user got bounced to /login mid-funnel and lost
  // their place). Force-refresh and retry once before surfacing the error.
  const tokenRefreshLink = onError(({ error, operation, forward }) => {
    if (typeof window === 'undefined') return
    if (!CombinedGraphQLErrors.is(error)) return
    const hasAuthError = error.errors.some(
      (e) => e.extensions?.code === 'UNAUTHENTICATED'
    )
    if (!hasAuthError) return
    const ctx = operation.getContext() as { tokenRefreshAttempted?: boolean }
    if (ctx.tokenRefreshAttempted) return
    operation.setContext({ ...ctx, tokenRefreshAttempted: true })

    return defer(async () => {
      const [, { getAuth }] = await Promise.all([
        import('./firebase/firebaseClient'),
        import('firebase/auth')
      ])
      const fbUser = getAuth().currentUser
      if (!fbUser) return null
      const fresh = await fbUser.getIdToken(true)
      await fetch('/api/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: fresh }),
        credentials: 'include'
      })
      // authLink sits UPSTREAM of this link, so `forward` would resend the
      // headers it computed for the first attempt — the empty or stale token
      // that just failed. A query fired before Firebase restored the session
      // (e.g. a hard load of /dashboard/host/pagos) retried with no token and
      // stayed UNAUTHENTICATED. Swap the fresh token in before retrying.
      operation.setContext(({ headers }) => ({
        headers: { ...headers, authorization: `Bearer ${fresh}` }
      }))
      return fresh
    }).pipe(mergeMap(() => forward(operation)))
  })

  const retryLink = new RetryLink({
    // Honor Retry-After header when the server provides one (e.g. 429s).
    // Otherwise use exponential backoff with jitter capped at 10s so
    // static builds can recover from transient rate limits.
    delay: (count, operation, error) => {
      const retryAfter = extractRetryAfterMs(error)
      if (retryAfter !== null) return retryAfter
      const base = Math.min(300 * 2 ** (count - 1), 10_000)
      return base + Math.random() * 300
    },
    attempts: {
      max: 5,
      retryIf: (error) => {
        if (!error) return false
        const status = extractStatus(error)
        // Retry network errors (no status) and transient failures:
        // 408 Request Timeout, 425 Too Early, 429 Rate Limit, and all 5xx.
        if (typeof status !== 'number') return true
        if (status === 408 || status === 425 || status === 429) return true
        if (status >= 500) return true
        // Other 4xx (400/401/403/404/…) won't succeed on retry and would
        // cause cascading re-renders on search pages.
        return false
      }
    }
  })
  const linkChain = new PersistedQueryLink({ sha256 })
    .concat(authLink)
    .concat(tokenRefreshLink)
    .concat(retryLink)
    .concat(httpLink)

  // Merge photo fields by id — if the photo id changes (user uploaded a
  // new photo), replace entirely to avoid stale fields. If it's the same
  // photo queried with different field selections, merge to preserve fields.
  const photoFieldMerge = {
    merge: (
      existing: { id?: string } | undefined,
      incoming: { id?: string } | undefined
    ) => {
      if (!existing) return incoming
      if (!incoming) return existing
      if (existing.id !== incoming.id) return incoming
      return { ...existing, ...incoming }
    }
  }

  const client = new ApolloClient({
    ssrMode:
      !!context ||
      typeof window === 'undefined' ||
      typeof document === 'undefined',

    // Preserve AC3 default — AC4 changed this to true which causes extra loading states
    defaultOptions: {
      watchQuery: { notifyOnNetworkStatusChange: false }
    },

    link: linkChain,

    cache: new InMemoryCache({
      typePolicies: {
        Location: { keyFields: false },
        // Photo must not be normalized — backend returns same photo references
        // across different users/entities causing cross-contamination bugs
        // (e.g. one user's profile picture appearing on another user's card).
        Photo: { keyFields: false },
        // Projection/view types whose `id` collides with another entity's id
        // (e.g. IndividualHost.id is a User.id, ChatParticipant.id references
        // User/Team, PastDueInvoiceUser.id is a User.id). Normalizing them
        // would mix partial fields into the canonical entity's cache entry.
        IndividualHost: { keyFields: false },
        ChatParticipant: { keyFields: false },
        PastDueInvoiceUser: { keyFields: false },
        PastDueInvoiceTeam: { keyFields: false },
        // Chubb catalog options are VALUE objects scoped to their own catalog,
        // not entities. `_CPISO` (ids 1-15), `_CSOTANO` (0-3) and
        // `_CMEDIDASSEGURIDAD` (11-14) all reuse small ids, so normalizing by
        // `id` let the last catalog written win: "Medidas de seguridad"
        // rendered "13 Pisos / 14 Pisos / 11 Pisos / 12 Pisos" instead of
        // "Caja Fuerte / Empresa de seguridad… / Vigilante propio 24 horas…".
        InmuebleCatalogOption: { keyFields: false },
        Checkout: {
          fields: {
            messages: { merge: (_, incoming) => incoming }
          }
        },
        User: {
          fields: {
            paymentMethods: { merge: (_, incoming) => incoming },
            reviews: { merge: (_, incoming) => incoming },
            listings: { merge: (_, incoming) => incoming },
            profilePicture: photoFieldMerge,
            identification: photoFieldMerge,
            constanciaFiscal: photoFieldMerge
          }
        },
        Team: {
          fields: {
            members: { merge: (_, incoming) => incoming },
            listings: { merge: (_, incoming) => incoming },
            reservations: { merge: (_, incoming) => incoming },
            logo: photoFieldMerge,
            icon: photoFieldMerge,
            constanciaFiscal: photoFieldMerge
          }
        },
        Chat: {
          fields: {
            members: { merge: (_, incoming) => incoming },
            messages: { merge: (_, incoming) => incoming }
          }
        }
      }
    })
  })

  return client
}

export { removeTokenCookie, TOKEN_COOKIE_OPTIONS }
export default getApolloClient
